import { getGoogleClientId } from '../config/auth';
import { logger } from './logger';

const log = logger.child('googleAuth');

// Capacitor modules - loaded dynamically when needed
let Capacitor = null;
let GoogleAuth = null;
let capacitorLoadAttempted = false;

// Lazy load Capacitor modules
const loadCapacitorModules = async () => {
  if (capacitorLoadAttempted) {
    return { Capacitor, GoogleAuth };
  }
  
  capacitorLoadAttempted = true;
  
  try {
    const capacitorCore = await import('@capacitor/core');
    Capacitor = capacitorCore.Capacitor;

    if (Capacitor && Capacitor.isNativePlatform()) {
      const googleAuthModule = await import('@southdevs/capacitor-google-auth');
      GoogleAuth = googleAuthModule.GoogleAuth;
      
      // Initialize Google Auth plugin
      if (GoogleAuth) {
        GoogleAuth.initialize({
          clientId: getGoogleClientId(),
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });
      }
    }
  } catch (e) {
    // Capacitor packages not available - use stubs
    log.debug('Capacitor modules not available, using web mode');
    Capacitor = {
      isNativePlatform: () => false,
      getPlatform: () => 'web'
    };
    GoogleAuth = null;
  }
  
  return { Capacitor, GoogleAuth };
};

// Check if running in Electron
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI;
};

/**
 * Sign in with Google - handles Electron, Capacitor, and web implementations
 * @returns {Promise<{credential?: string, idToken?: string, email?: string, name?: string}>}
 */
export const signInWithGoogle = async () => {
  // Electron authentication using OAuth popup
  if (isElectron()) {
    try {
      log.debug('Starting Electron OAuth flow');
      const result = await window.electronAPI.googleOAuth();

      if (!result.success) {
        throw new Error(result.error || 'OAuth failed');
      }

      log.debug('Electron Google OAuth result received');
      return {
        idToken: result.idToken,
        accessToken: result.accessToken
      };
    } catch (error) {
      log.error('OAuth failed', error, { phase: 'electron' });
      throw new Error(error.message || 'Google Sign-In failed');
    }
  }
  
  // Native mobile authentication using Capacitor plugin
  const { Capacitor: Cap, GoogleAuth: GA } = await loadCapacitorModules();
  if (Cap && Cap.isNativePlatform() && GA) {
    try {
      const result = await GA.signIn();
      log.debug('Native Google Sign-In result received');

      return {
        idToken: result.authentication.idToken,
        email: result.email,
        name: result.name,
        imageUrl: result.imageUrl,
        serverAuthCode: result.serverAuthCode
      };
    } catch (error) {
      log.error('OAuth failed', error, { phase: 'native' });
      throw new Error(error.message || 'Google Sign-In failed');
    }
  }
  
  // Web authentication (handled by the existing Google Sign-In button)
  // This will be triggered by the button callback, not directly called
  throw new Error('Web sign-in should use the Google Sign-In button');
};

/**
 * Sign out from Google
 */
export const signOutFromGoogle = async () => {
  const { Capacitor: Cap, GoogleAuth: GA } = await loadCapacitorModules();
  if (Cap && Cap.isNativePlatform() && GA) {
    try {
      await GA.signOut();
      log.debug('Signed out from Google (native)');
    } catch (error) {
      log.warn('signOut failed', error, { phase: 'native' });
    }
  }
  // Web sign-out is handled by clearing localStorage token
};

/**
 * Check if Google Auth is available
 */
export const isGoogleAuthAvailable = async () => {
  const { Capacitor: Cap } = await loadCapacitorModules();
  if (Cap && Cap.isNativePlatform()) {
    return true; // Native plugin is always available after initialization
  }
  // For web, check if the Google Sign-In library is loaded
  return typeof window !== 'undefined' && window.google && window.google.accounts;
};

